import $ from 'jquery';

class MegaMenu {
  constructor() {
    // Desktop nav wrapper
    this.navDesktop = document.querySelector('.nav--desktop');
    if (!this.navDesktop) return;

    // Read menu type from data attribute, default to megamenu
    this.menuType = this.navDesktop.getAttribute('data-menu-type') || 'megamenu';

    this.navItems = $('.nav--desktop > .nav > .menu > .menu-item-has-children');
    this.searchIcon = document.querySelector('.nav-search');
    this.search = $('#header-search');
    this.resizeTimer = null;

    // Init appropriate behaviour
    if (this.menuType === 'hamburger') {
      this.initHamburgerMenu();
    } else if (this.menuType === 'hamburger-side-open') {
      this.initHamburgerSideMenu();
    } else if (this.menuType === 'cascading') {
      this.initCascadingMenu();
    } else {
      this.initMegaMenu();
    }

    this.bindResize();
    this.initSearchToggle();
  }

  /* -----------------------
   * ALIGNMENT HELPERS
   * --------------------- */

  resetCol2Alignment(megaContainer) {
    megaContainer.querySelectorAll('.hamburger-menu__col--2').forEach((col) => {
      col.style.transform = '';
      col.style.marginTop = '';
      col.style.top = '';
    });
  }

  resetCol3Alignment(megaContainer) {
    megaContainer.querySelectorAll('.hamburger-menu__col--3').forEach((col) => {
      col.style.transform = '';
      col.style.marginTop = '';
      col.style.top = '';
      col.style.maxHeight = '';
      col.style.overflowY = '';
    });
  }

  getMenuViewportBounds(megaContainer) {
    const menuEl = megaContainer.closest('.hamburger-menu');
    const padding = 24;

    if (!menuEl) {
      return {
        top: padding,
        bottom: window.innerHeight - padding,
      };
    }

    const rect = menuEl.getBoundingClientRect();
    return {
      top: rect.top + padding,
      bottom: rect.bottom - padding,
    };
  }

  applyColumnOffset(col, delta) {
    if (Math.abs(delta) <= 0.5) return;

    col.style.transform = `translateY(${delta}px)`;
    col.style.marginTop = `${delta}px`;
    col.style.top = `${delta}px`;
  }

  clampColumnToViewport(col, delta, bounds) {
    const colRect = col.getBoundingClientRect();
    let adjustedDelta = delta;

    let projectedBottom = colRect.bottom + adjustedDelta;
    if (projectedBottom > bounds.bottom) {
      adjustedDelta -= projectedBottom - bounds.bottom;
    }

    let projectedTop = colRect.top + adjustedDelta;
    if (projectedTop < bounds.top) {
      adjustedDelta += bounds.top - projectedTop;
    }

    return adjustedDelta;
  }

  constrainColumnHeight(col, bounds) {
    const colRect = col.getBoundingClientRect();
    const availableHeight = bounds.bottom - colRect.top;

    if (availableHeight > 0 && colRect.height > availableHeight) {
      col.style.maxHeight = `${availableHeight}px`;
      col.style.overflowY = 'auto';
    }
  }

  // Align visible Col 2 so its first LI lines up with the hovered parent LI (Level 1)
  alignCol2WithParent(megaContainer, parentItem, col2) {
    if (!parentItem || !col2) return;

    this.resetCol2Alignment(megaContainer);

    const firstChildLi = col2.querySelector(':scope > li') || col2.querySelector('li');
    if (!firstChildLi) return;

    const parentRect = parentItem.getBoundingClientRect();
    const firstChildRect = firstChildLi.getBoundingClientRect();

    const delta = parentRect.top - firstChildRect.top;

    if (Math.abs(delta) > 0.5) {
      this.applyColumnOffset(col2, delta);
    }
  }

  // Align Col 3 with the hovered child (Level 2).
  // Top-align by default; bottom-align when the submenu would overflow the menu viewport.
  alignCol3WithChild(megaContainer, childItem, col3) {
    if (!childItem || !col3) return;

    this.resetCol3Alignment(megaContainer);

    const firstGrandchildLi = col3.querySelector(':scope > li') || col3.querySelector('li');
    const lastGrandchildLi = col3.querySelector(':scope > li:last-child') || firstGrandchildLi;
    if (!firstGrandchildLi || !lastGrandchildLi) return;

    const childRect = childItem.getBoundingClientRect();
    const firstGrandchildRect = firstGrandchildLi.getBoundingClientRect();
    const lastGrandchildRect = lastGrandchildLi.getBoundingClientRect();
    const col3Rect = col3.getBoundingClientRect();
    const bounds = this.getMenuViewportBounds(megaContainer);

    const topAlignDelta = childRect.top - firstGrandchildRect.top;
    const bottomAlignDelta = childRect.bottom - lastGrandchildRect.bottom;

    let delta = topAlignDelta;
    const projectedBottomWithTopAlign = col3Rect.bottom + topAlignDelta;

    if (projectedBottomWithTopAlign > bounds.bottom) {
      delta = bottomAlignDelta;
    }

    delta = this.clampColumnToViewport(col3, delta, bounds);
    this.applyColumnOffset(col3, delta);
    this.constrainColumnHeight(col3, bounds);
  }

  /* ======================
   * HAMBURGER MENU (Side-Open/Multi-Column)
   * ==================== */
  initHamburgerSideMenu() {
    const burger = this.navDesktop.querySelector('.nav__burger');
    const menu = document.querySelector('.hamburger-menu');

    if (!burger || !menu) return;

    if (burger.dataset.megamenuBound === 'true') return;
    burger.dataset.megamenuBound = 'true';

    const body = document.body;

    const setState = (isOpen) => {
      menu.classList.toggle('is-active', isOpen);
      burger.classList.toggle('is-active', isOpen);
      burger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      body.classList.toggle('has-hamburger-open', isOpen);
      body.style.overflow = isOpen ? 'hidden' : '';
    };

    burger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = menu.classList.contains('is-active');
      setState(!isOpen);
    });

    menu.querySelectorAll('[data-hamburger-close]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        setState(false);
      });
    });

    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) setState(false);
    });

    document.addEventListener('click', (e) => {
      if (!menu.classList.contains('is-active')) return;
      if (menu.contains(e.target) || burger.contains(e.target)) return;
      setState(false);
    });

    const megaContainer = document.querySelector('.hamburger-menu__mega');
    if (!megaContainer) return;

    // 1) PARENT HOVER (Level 1) → show matching col2 + align it
    megaContainer.addEventListener(
      'mouseenter',
      (e) => {
        const parentItem = e.target.closest('.hamburger-menu__parent-item.has-children');
        if (!parentItem) return;

        const menuId = parentItem.dataset.menuId;

        // Reset col3 (except persistent) + reset hovered child states
        megaContainer.querySelectorAll('.hamburger-menu__col--3').forEach((col) => {
          if (!col.classList.contains('is-active-by-current')) col.classList.remove('is-visible');
        });
        megaContainer.querySelectorAll('.hamburger-menu__child-item').forEach((i) =>
          i.classList.remove('is-hovered')
        );

        // Reset both alignments (so we don't stack offsets)
        this.resetCol2Alignment(megaContainer);
        this.resetCol3Alignment(megaContainer);

        // Show matching Col 2
        let activeCol2 = null;
        megaContainer.querySelectorAll('.hamburger-menu__col--2').forEach((col) => {
          const isMatch = col.dataset.parentId === menuId;
          col.classList.toggle('is-visible', isMatch);
          if (isMatch) activeCol2 = col;
        });

        // Hover state for level 1 items
        megaContainer.querySelectorAll('.hamburger-menu__parent-item').forEach((i) => i.classList.remove('is-hovered'));
        parentItem.classList.add('is-hovered');

        // Align the visible col2 to this parent item
        if (activeCol2) {
          this.alignCol2WithParent(megaContainer, parentItem, activeCol2);
        }
      },
      true
    );

    // 2) CHILD HOVER (Level 2) → show matching col3 + align it
    megaContainer.addEventListener(
      'mouseenter',
      (e) => {
        const childItem = e.target.closest('.hamburger-menu__child-item');
        if (!childItem) return;

        const menuId = childItem.dataset.menuId;
        const hasGrandchildren =
          childItem.classList.contains('has-grandchildren') ||
          childItem.classList.contains('menu-item-has-children');

        megaContainer.querySelectorAll('.hamburger-menu__col--3').forEach((col) => {
          if (!col.classList.contains('is-active-by-current')) col.classList.remove('is-visible');
        });

        megaContainer.querySelectorAll('.hamburger-menu__child-item').forEach((i) => i.classList.remove('is-hovered'));
        childItem.classList.add('is-hovered');

        if (hasGrandchildren) {
          // Reset previous col3 alignment before showing the next
          this.resetCol3Alignment(megaContainer);

          let activeCol3 = null;
          megaContainer.querySelectorAll('.hamburger-menu__col--3').forEach((col) => {
            const isMatch = col.dataset.childId === menuId;
            col.classList.toggle('is-visible', isMatch);
            if (isMatch) activeCol3 = col;
          });

          if (activeCol3) {
            this.alignCol3WithChild(megaContainer, childItem, activeCol3);
          }
        } else {
          // No grandchildren: clear any previous shift so nothing looks odd
          this.resetCol3Alignment(megaContainer);
        }
      },
      true
    );

    // 3) MOUSE LEAVE
    megaContainer.addEventListener('mouseleave', () => {
      megaContainer.querySelectorAll('.hamburger-menu__col--2, .hamburger-menu__col--3').forEach((col) => {
        if (!col.classList.contains('is-active-by-current')) col.classList.remove('is-visible');
      });

      megaContainer
        .querySelectorAll('.hamburger-menu__parent-item, .hamburger-menu__child-item')
        .forEach((i) => i.classList.remove('is-hovered'));

      // Reset any shifts
      this.resetCol2Alignment(megaContainer);
      this.resetCol3Alignment(megaContainer);
    });

    // 4) PERSIST ACTIVE STATE (on load)
    // Part 1: Keep Column 2 open + align it
    document
      .querySelectorAll('.hamburger-menu__parent-item.is-active, .hamburger-menu__parent-item.current-menu-ancestor')
      .forEach((item) => {
        const menuId = item.dataset.menuId;
        const col2 = megaContainer.querySelector(`.hamburger-menu__col--2[data-parent-id="${menuId}"]`);
        if (col2) {
          col2.classList.add('is-visible', 'is-active-by-current');
          // Align on load (nice if menu opens to current section)
          this.alignCol2WithParent(megaContainer, item, col2);
        }
      });

    // Part 2: Keep Column 3 open + align it
    document
      .querySelectorAll('.hamburger-menu__child-item.is-active, .hamburger-menu__child-item.current-menu-ancestor')
      .forEach((item) => {
        const menuId = item.dataset.menuId;
        const col3 = megaContainer.querySelector(`.hamburger-menu__col--3[data-child-id="${menuId}"]`);
        if (col3) {
          col3.classList.add('is-visible', 'is-active-by-current');
          this.alignCol3WithChild(megaContainer, item, col3);
        }
      });
  }

  initHamburgerMenu() {
    const burger = this.navDesktop.querySelector('.nav__burger');
    const menu = document.querySelector('.hamburger-menu');
    if (!burger || !menu) return;
    if (burger.dataset.megamenuBound === 'true') return;
    burger.dataset.megamenuBound = 'true';

    const body = document.body;
    const setState = (isOpen) => {
      menu.classList.toggle('is-active', isOpen);
      burger.classList.toggle('is-active', isOpen);
      body.classList.toggle('has-hamburger-open', isOpen);
    };

    burger.addEventListener('click', (e) => {
      e.preventDefault();
      setState(!menu.classList.contains('is-active'));
    });
  }

  initMegaMenu() {
    if (!this.navItems.length) return;
    this.navItems.each(function () {
      const $item = $(this);
      $item.on('mouseenter.megamenu', () => $item.addClass('is-open'));
      $item.on('mouseleave.megamenu', () => $item.removeClass('is-open'));
    });
  }

  initCascadingMenu() {
    if (!this.navItems.length) return;
    this.navItems.each(function () {
      const $item = $(this);
      $item.children('a')
        .first()
        .on('click.cascading', function (e) {
          if (!$item.parent().hasClass('menu')) return;
          e.preventDefault();
          $item.toggleClass('is-open');
        });
    });
  }

  initSearchToggle() {
    if (!this.searchIcon || !this.search.length) return;
    this.searchIcon.addEventListener('click', (e) => {
      e.preventDefault();
      const isOpen = this.search.hasClass('is-active');
      this.search.toggleClass('is-active', !isOpen);
      if (!isOpen) {
        const input = this.search.find('input[type="search"], input[type="text"]')[0];
        if (input) input.focus();
      }
    });
  }

  bindResize() {
    window.addEventListener('resize', () => {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {}, 150);
    });
  }
}

let megaMenuInitialised = false;
const initMegaMenuOnce = () => {
  if (megaMenuInitialised) return;
  megaMenuInitialised = true;
  new MegaMenu();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegaMenuOnce, { once: true });
} else {
  initMegaMenuOnce();
}

export default MegaMenu;